import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  ClipboardCheck,
  FileCheck,
  Landmark,
  Leaf,
  MessageCircle,
  ScanSearch,
  ShieldCheck,
  TreeDeciduous,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  managedOfferings,
  selfServeOfferings,
  type OfferingIconKey,
  type PublicOffering,
} from "@/lib/publicOfferings";

const icons: Record<OfferingIconKey, LucideIcon> = {
  lc: FileCheck,
  sanctions: ShieldCheck,
  cbam: Leaf,
  eudr: TreeDeciduous,
  decision: ClipboardCheck,
  duty: Landmark,
  audit: ScanSearch,
};

function statusClasses(status: PublicOffering["status"]): string {
  if (status === "Live") {
    return "border-[#B2F273]/25 bg-[#B2F273]/10 text-[#B2F273]";
  }
  if (status === "Private Preview") {
    return "border-sky-300/25 bg-sky-300/10 text-sky-200";
  }
  return "border-amber-300/25 bg-amber-300/10 text-amber-200";
}

function OfferingCard({ offering }: { offering: PublicOffering }) {
  const Icon = icons[offering.iconKey];

  return (
    <Link
      to={offering.href}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EDF5F2]/10 bg-[#00382E]/45 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#B2F273]/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B2F273]"
    >
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#B2F273]/20 bg-[#B2F273]/10">
          <Icon className="h-6 w-6 text-[#B2F273]" />
        </div>
        <span
          className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${statusClasses(
            offering.status,
          )}`}
        >
          {offering.status}
        </span>
      </div>

      <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[#EDF5F2]/40">
        {offering.brand ? `${offering.brand} | ${offering.tagline}` : offering.tagline}
      </p>
      <h3 className="font-display text-2xl font-bold text-white transition-colors group-hover:text-[#B2F273]">
        {offering.name}
      </h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#EDF5F2]/60">
        {offering.description}
      </p>

      <div className="mt-6 flex items-end justify-between gap-4 border-t border-[#EDF5F2]/10 pt-5">
        <span className="text-xs font-medium text-[#EDF5F2]/50">{offering.price}</span>
        <span className="inline-flex items-center text-right text-sm font-semibold text-[#B2F273]">
          {offering.cta}
          <ArrowRight className="ml-1.5 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function OfferingGroup({
  id,
  eyebrow,
  title,
  description,
  offerings,
}: {
  id: string;
  eyebrow: string;
  title: string;
  description: string;
  offerings: PublicOffering[];
}) {
  return (
    <div id={id} className="scroll-mt-24">
      <div className="mb-9 grid gap-5 lg:grid-cols-[0.75fr_1.25fr] lg:items-end">
        <div>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-[#B2F273]">
            {eyebrow}
          </p>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">{title}</h2>
        </div>
        <p className="max-w-2xl text-base leading-relaxed text-[#EDF5F2]/60 lg:justify-self-end">
          {description}
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {offerings.map((offering) => (
          <OfferingCard key={offering.name} offering={offering} />
        ))}
      </div>
    </div>
  );
}

export function ToolsSection() {
  return (
    <section className="relative overflow-hidden bg-[#00261C] py-24">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(178,242,115,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(178,242,115,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_72%)]" />
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl space-y-24">
          <OfferingGroup
            id="self-serve"
            eyebrow="Run it yourself"
            title="Self-serve software"
            description="Use these tools when the input is defined and the output can be checked, cited, and exported without a managed investigation."
            offerings={selfServeOfferings}
          />
          <OfferingGroup
            id="managed"
            eyebrow="Enso operates with you"
            title="Managed intelligence"
            description="Use these solutions when evidence is fragmented, exceptions need investigation, or your team needs an accountable delivery rather than another dashboard."
            offerings={managedOfferings}
          />

          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl border border-[#EDF5F2]/10 bg-[#00382E]/50 p-7 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#B2F273]/20 bg-[#B2F273]/10">
                <MessageCircle className="h-5 w-5 text-[#B2F273]" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-white">
                  Have a rules question? Ask RulGPT.
                </h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#EDF5F2]/60">
                  Get cited answers on UCP 600, ISBP 821, URDG 758, ISP98, and supported
                  sanctions rules.
                </p>
              </div>
            </div>
            <Button
              asChild
              className="shrink-0 border-none bg-[#EDF5F2]/10 text-white hover:bg-[#EDF5F2]/15"
            >
              <a href="https://tfrules.com" target="_blank" rel="noopener noreferrer">
                Open RulGPT
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
